/**
 * Display formatters for the CRO scorecard tiles.
 *
 * Carried forward verbatim from packages/spa/src/lib/format.ts.
 * The SPA renders SF-native + Rillet shapes; this module owns the locale-aware
 * conversion. The dashboard's audience is internal Adeptia executives,
 * so en-US locale + USD currency are the v1 defaults.
 */

/**
 * Format a numeric pipeline value as compact currency (e.g., $4.2M, $850K).
 *
 * `Intl.NumberFormat({ notation: 'compact' })` produces 1–2 significant digits
 * which is what the headline tile actually wants.
 */
export function formatCurrency(value: number, currency = 'USD'): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  return formatter.format(value);
}

/** Standard (non-compact) currency formatter for drilldown rows. */
export function formatAmount(value: number, currency = 'USD'): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
  return formatter.format(value);
}

/**
 * YoY-comparison helper. Returns the percent delta + sign so the tile can
 * render "vs $2.8M YoY (+50%)" without re-implementing the math.
 *
 * Edge cases:
 * - prior === 0 -> sign is '+' if current > 0, '0' if both zero. We do not
 *   compute an Infinity percentage; the SPA shows "—" instead.
 */
export interface YoYDelta {
  pct: number; // Whole percent (e.g., 50 not 0.5)
  sign: '+' | '-' | '0';
  hasComparable: boolean; // false when prior === 0
}

export function formatYoYDelta(current: number, prior: number): YoYDelta {
  if (prior === 0) {
    return {
      pct: 0,
      sign: current > 0 ? '+' : '0',
      hasComparable: false,
    };
  }
  const ratio = (current - prior) / prior;
  const pct = Math.round(Math.abs(ratio) * 100);
  if (ratio > 0) return { pct, sign: '+', hasComparable: true };
  if (ratio < 0) return { pct, sign: '-', hasComparable: true };
  return { pct: 0, sign: '0', hasComparable: true };
}

/** Format the contributing-opp count as a friendly singular/plural label. */
export function formatOppCount(n: number): string {
  if (n === 1) return '1 opportunity';
  return `${String(n)} opportunities`;
}

/** Format a SF ISO date string ("2026-06-30") as "Jun 30, 2026". */
export function formatCloseDate(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.valueOf())) return isoDate;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
}

/**
 * Format a plain number (count / ratio) compactly for CRO trend tiles.
 * e.g., 28.5 → "28.5", 100 → "100"
 */
export function formatCount(value: number, decimals = 1): string {
  return value.toFixed(decimals);
}

/**
 * Format a count / volume metric as a thousands-separated INTEGER, no decimals
 * (quick-260626-j5u Task 1). Count metrics (outbound, meetings booked/delivered,
 * email/call/linkedin totals, new leads, etc.) are inherently whole numbers, so
 * "11.9k"/"2740.0"/"39.0" must render as "11,900"/"2,740"/"39" — never with a
 * trailing ".0". Rounds to the nearest integer (defensive against float drift)
 * and groups thousands per the en-US locale. Distinct from `formatCount`
 * (decimal-aware ratio formatter) and `formatCurrency` (compact $).
 */
export function formatCountInt(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
    Math.round(value),
  );
}

/**
 * Compact whole-unit currency for headline KPIs (quick-260625-nem Task 4b).
 * Rounds to the nearest $1k and renders as "$Nk"; values >= $1M render as
 * "$N.NM" (one decimal, trailing .0 trimmed). Examples:
 *   48548   → "$49k"
 *   1250    → "$1k"
 *   500     → "$1k"      (rounds up to nearest 1k; 0 stays "$0k")
 *   1900500 → "$1.9M"
 *   2000000 → "$2M"
 *
 * Distinct from `formatCurrency` (Intl compact, 1-2 sig digits) — this is the
 * exec-headline "round to $k" formatter the MAIN-12 Avg $$/Opp tile wants.
 */
export function formatCompactCurrencyK(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const millions = abs / 1_000_000;
    // One decimal, drop a trailing ".0" (2.0 → "2", 1.9 → "1.9").
    const m = millions.toFixed(1).replace(/\.0$/, '');
    return `${sign}$${m}M`;
  }
  const thousands = Math.round(abs / 1000);
  return `${sign}$${String(thousands)}k`;
}

/**
 * The U+2212 MINUS SIGN, written as an ESCAPE SEQUENCE on purpose.
 *
 * D-06 negative figures render with a real minus sign, not an ASCII hyphen, so
 * they line up under the dollar figures above them. As a literal character it
 * is visually indistinguishable from '-' in an editor and a later copy-paste
 * would silently downgrade it; as an escape it cannot be swapped by accident.
 * test/lib/format.test.ts asserts the CODE POINT (0x2212), not the glyph.
 */
const MINUS_SIGN = '\u2212';

/**
 * D-06 — the raw signed over/under that accompanies every budget percentage,
 * e.g. "187% of the $729,000 ARR budget * +$632,500".
 *
 * Rounds to a whole dollar, then prefixes '+' at or above zero and the U+2212
 * MINUS SIGN below it. Zero reads as positive. The dollar figure itself comes
 * from `formatAmount` rather than a second Intl.NumberFormat configuration, so
 * there is exactly one currency-grouping path in this module.
 */
export function signed(n: number): string {
  const rounded = Math.round(n);
  const sign = rounded < 0 ? MINUS_SIGN : '+';
  return `${sign}${formatAmount(Math.abs(rounded))}`;
}

/**
 * D-06 — a reading as a whole percent of its budget, e.g. `pctOf(1361500,
 * 729000)` is "187%".
 *
 * Returns null when there is no usable denominator (null or zero budget) so the
 * caller can render an em dash rather than an Infinity or a NaN. A negative
 * percentage — a quarter that nets below zero — carries the same U+2212 MINUS
 * SIGN as `signed`, so the two never disagree on screen.
 */
export function pctOf(n: number, of: number | null): string | null {
  if (of === null || of === 0) return null;
  const rounded = Math.round((n / of) * 100);
  if (rounded < 0) return `${MINUS_SIGN}${String(Math.abs(rounded))}%`;
  return `${String(rounded)}%`;
}
